// Products — Supabase implementation. Catalog only; stock lives per-event.

import { supabase } from '@/lib/supabase';
import { mapProduct } from './mappers';
import { chunkIds, mapLimit, USAGE_CONCURRENCY, type BulkHardDeleteResult } from './bulk';
import { removeStoredImage } from '@/lib/productImages';
import type { Product } from '@/types/domain';
import type { ActorFields } from './actorFields';

export interface ListProductsOptions {
  isActive?: boolean;
  categoryId?: string;
}

export async function listProducts(opts: ListProductsOptions = {}): Promise<Product[]> {
  let q = supabase.from('products').select('*').order('name');
  if (opts.isActive !== undefined) q = q.eq('is_active', opts.isActive);
  if (opts.categoryId) q = q.eq('category_id', opts.categoryId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []).map(mapProduct);
}

export async function getProduct(id: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('products').select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapProduct(data) : null;
}

export interface DeleteProductInput extends ActorFields {
  id?: string;
  productId?: string;
}

export interface CreateProductInput extends ActorFields {
  sku: string;
  name: string;
  price: number;
  costPrice?: number;
  categoryId?: string | null;
  imageUrl?: string | null;
  description?: string | null;
  isActive?: boolean;
  /** Packaging label — pcs, box, botol. Column is NOT NULL, so '' becomes 'pcs'. */
  unit?: string | null;
  /** Free text. '' becomes null, which is what the column's check expects. */
  brand?: string | null;
}

function rowFrom(input: Partial<CreateProductInput>) {
  const r: Record<string, unknown> = {};
  if (input.sku !== undefined) r.sku = input.sku.trim();
  if (input.name !== undefined) r.name = input.name.trim();
  if (input.price !== undefined) r.price = Math.round(input.price);
  if (input.costPrice !== undefined) r.cost_price = input.costPrice ?? null;
  if (input.imageUrl !== undefined) r.image_url = input.imageUrl || null;
  if (input.description !== undefined) r.description = input.description || null;
  if (input.isActive !== undefined) r.is_active = input.isActive;
  if (input.unit !== undefined) r.unit = input.unit?.trim() || 'pcs';
  if (input.brand !== undefined) r.brand = input.brand?.trim() || null;
  if (input.categoryId !== undefined) {
    // A uuid means a real categories row; anything else is legacy free text
    // kept so pre-Categories products keep their grouping.
    const v = input.categoryId;
    const isUuid = !!v && /^[0-9a-f-]{36}$/i.test(v);
    r.category_id = isUuid ? v : null;
    r.legacy_category = isUuid ? null : v || null;
  }
  return r;
}

/**
 * Thrown when a SKU is already taken. Carries the owning product so the UI can
 * say *which* one — and, crucially, whether it is deactivated. The unique
 * index is on lower(sku) and the Inventory list defaults to active products
 * only, so a SKU held by a deactivated product looks free on screen and then
 * fails on save with nothing to point at. That is the silent error the owner
 * kept hitting.
 */
export class SkuConflictError extends Error {
  constructor(readonly product: Product, message: string) {
    super(message);
    this.name = 'SkuConflictError';
  }
}

/** Case-insensitive SKU lookup, active or not. */
export async function findProductBySku(sku: string): Promise<Product | null> {
  // ilike treats % and _ as wildcards; a SKU may legitimately contain them.
  const pattern = sku.trim().replace(/[\\%_]/g, (c) => `\\${c}`);
  const { data, error } = await supabase
    .from('products').select('*').ilike('sku', pattern).limit(1).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapProduct(data) : null;
}

async function skuConflictError(sku: string): Promise<Error> {
  const p = await findProductBySku(sku).catch(() => null);
  if (!p) return new Error(`SKU "${sku}" sudah dipakai`);
  return new SkuConflictError(
    p,
    p.isActive
      ? `SKU "${sku}" sudah dipakai oleh produk "${p.name}".`
      : `SKU "${sku}" sudah dipakai oleh produk "${p.name}" yang sedang NONAKTIF, jadi tidak tampil di daftar. Aktifkan kembali produk itu, atau pakai SKU lain.`,
  );
}

export async function createProduct(input: CreateProductInput): Promise<Product> {
  const { data, error } = await supabase
    .from('products').insert(rowFrom(input) as never).select('*').single();
  if (error) {
    if (error.code === '23505') throw await skuConflictError(input.sku);
    throw new Error(error.message);
  }
  return mapProduct(data);
}

export type UpdateProductInput = Partial<CreateProductInput>;

export async function updateProduct(id: string, input: UpdateProductInput): Promise<void> {
  const { error } = await supabase.from('products').update(rowFrom(input) as never).eq('id', id);
  if (error) {
    // Names the SKU actually being written, not `input.sku` — this is a partial
    // patch, so on any update that does not touch the SKU that field is
    // undefined and the message read `SKU "undefined" sudah dipakai`. The
    // import's update path makes that the common case, not the rare one.
    if (error.code === '23505') {
      throw new Error(
        input.sku
          ? `SKU "${input.sku}" sudah dipakai produk lain`
          : 'SKU bentrok dengan produk lain',
      );
    }
    throw new Error(error.message);
  }
}

/** Soft delete — allocations and invoice lines still reference this row. */
export async function deleteProduct(input: string | DeleteProductInput): Promise<void> {
  const id = typeof input === 'string' ? input : (input.id ?? input.productId);
  if (!id) throw new Error('id produk wajib diisi');
  const { error } = await supabase.from('products').update({ is_active: false }).eq('id', id);
  if (error) throw new Error(error.message);
}
export const deleteProductSimple = deleteProduct;

export interface ProductUsage {
  allocations: number;
  transactionItems: number;
  returnItems: number;
  promoItems: number;
}

/**
 * How many rows reference this product. Zero everywhere = never used.
 *
 * event_promo_items is ON DELETE RESTRICT, so the database would refuse the
 * delete anyway. It is counted so the refusal comes with its reason instead of
 * a bare foreign-key error -- and so a bulk delete can set such a product aside
 * rather than have it fail the whole batch it was sent in.
 */
export async function getProductUsage(id: string): Promise<ProductUsage> {
  const countIn = async (
    table: 'event_stock_allocations' | 'transaction_items' | 'return_items' | 'event_promo_items',
  ) => {
    const { count, error } = await supabase
      .from(table).select('*', { count: 'exact', head: true }).eq('product_id', id);
    if (error) throw new Error(error.message);
    return count ?? 0;
  };
  const [allocations, transactionItems, returnItems, promoItems] = await Promise.all([
    countIn('event_stock_allocations'),
    countIn('transaction_items'),
    countIn('return_items'),
    countIn('event_promo_items'),
  ]);
  return { allocations, transactionItems, returnItems, promoItems };
}

export const isProductUsed = (u: ProductUsage) =>
  u.allocations + u.transactionItems + u.returnItems + u.promoItems > 0;

export const describeProductUsage = (u: ProductUsage) =>
  `${u.allocations} alokasi stok event, ${u.promoItems} promo event, ${u.transactionItems} baris transaksi, ${u.returnItems} baris retur`;

/**
 * Hard delete — only for a product that was never used anywhere.
 *
 * The FK alone does not protect this: event_stock_allocations is ON DELETE
 * RESTRICT, but transaction_items and return_items are ON DELETE SET NULL,
 * so a product that was sold and later deallocated would delete cleanly and
 * leave invoice lines pointing at nothing. Usage is checked explicitly first.
 */
export async function hardDeleteProduct(id: string): Promise<void> {
  const usage = await getProductUsage(id);
  if (isProductUsed(usage)) {
    throw new Error(
      `Produk sudah pernah dipakai (${describeProductUsage(usage)}) — tidak bisa dihapus permanen. Nonaktifkan saja.`,
    );
  }
  const { error } = await supabase.from('products').delete().eq('id', id);
  if (error) {
    if (error.code === '23503') {
      throw new Error('Produk sudah dipakai di event/transaksi — nonaktifkan saja.');
    }
    throw new Error(error.message);
  }
}

export type BulkCreateProductItem = CreateProductInput;
export interface BulkCreateProductsInput extends ActorFields {
  items: BulkCreateProductItem[];
}
export interface BulkCreateProductsResult {
  created: number;
  /** Alias of `created`, for the import dialog's summary line. */
  createdCount: number;
  skipped: { sku: string; reason: string }[];
}

/**
 * CSV import. Inserted one row at a time on purpose: a single bad SKU in a
 * 500-row sheet should not roll back the other 499, and the caller wants to
 * show exactly which lines failed.
 */
export async function bulkCreateProducts(
  input: BulkCreateProductsInput,
): Promise<BulkCreateProductsResult> {
  const result: BulkCreateProductsResult = { created: 0, createdCount: 0, skipped: [] };
  for (const item of input.items) {
    try {
      await createProduct(item);
      result.created += 1;
      result.createdCount += 1;
    } catch (e) {
      result.skipped.push({ sku: item.sku, reason: (e as Error).message });
    }
  }
  return result;
}

// ── Bulk upsert ──────────────────────────────────────────────────────────────

/**
 * Every product's SKU, lowercased, paired with its id.
 *
 * Paged rather than reusing listProducts(), which caps at 500: past that the
 * import would stop recognising existing SKUs and start failing every row on a
 * unique-index violation it could have predicted. Inactive products are
 * included — their SKUs still occupy the index.
 */
async function fetchAllSkuIds(): Promise<Map<string, string>> {
  const PAGE = 1000;
  const m = new Map<string, string>();
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('products')
      .select('id, sku')
      .order('id')
      .range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    for (const r of batch) m.set(r.sku.trim().toLowerCase(), r.id);
    if (batch.length < PAGE) break;
  }
  return m;
}

export interface UpsertProductsResult {
  created: number;
  updated: number;
  failed: { sku: string; reason: string }[];
}

/**
 * Create or update by SKU — the round-trip half of export → edit → re-import.
 *
 * Matched on lower(sku) because that is what the unique index is built on
 * (`products_sku_key on products (lower(sku))`). Being an *expression* index,
 * PostgREST cannot infer it as a conflict target, so `.upsert()` is not
 * available and the branch is made here instead.
 *
 * Sequential on purpose: one bad row costs one row, and the caller can name it.
 * A SKU is never rewritten by an import — renaming one through a spreadsheet is
 * indistinguishable from creating a product, and would orphan the original.
 */
export async function upsertProducts(
  items: CreateProductInput[],
): Promise<UpsertProductsResult> {
  const existing = await fetchAllSkuIds();
  const out: UpsertProductsResult = { created: 0, updated: 0, failed: [] };

  for (const item of items) {
    const key = item.sku.trim().toLowerCase();
    const id = existing.get(key);
    try {
      if (id) {
        // `sku` deliberately withheld: it is the key, not a field to patch.
        const { sku: _sku, ...patch } = item;
        await updateProduct(id, patch);
        out.updated += 1;
      } else {
        const created = await createProduct(item);
        existing.set(key, created.id);
        out.created += 1;
      }
    } catch (e) {
      out.failed.push({ sku: item.sku, reason: e instanceof Error ? e.message : 'Gagal' });
    }
  }
  return out;
}

/**
 * Bulk deactivate or reactivate, for the checkbox selection on the Inventory
 * page.
 *
 * One request per chunk rather than one per product. The catalogue can hold a
 * thousand rows, and a row-at-a-time loop would both crawl and, if a request
 * failed partway, leave the job half done with nothing to say about it. The
 * error below carries how far it got instead.
 *
 * Permanent removal is hardDeleteUnusedProducts(), which only ever deletes
 * what nothing references.
 */
export async function setProductsActive(ids: string[], isActive: boolean): Promise<number> {
  const chunks = chunkIds(ids);
  const total = chunks.reduce((n, c) => n + c.length, 0);
  let done = 0;
  for (const chunk of chunks) {
    // .select() returns the rows actually changed. Without it, rows the
    // caller may not update (RLS) are skipped silently and the request still
    // "succeeds" — the screen would say done while nothing changed.
    const { data, error } = await supabase
      .from('products').update({ is_active: isActive }).in('id', chunk).select('id');
    if (error) {
      throw new Error(`${done} dari ${total} produk diproses, lalu gagal: ${error.message}`);
    }
    const changed = data?.length ?? 0;
    done += changed;
    if (changed < chunk.length) {
      throw new Error(
        `${done} dari ${total} produk diproses; ${chunk.length - changed} tidak bisa diubah — kemungkinan akun ini tidak punya hak untuk itu.`,
      );
    }
  }
  return done;
}

/**
 * Bulk permanent delete -- of the products nothing references, and only those.
 *
 * Usage is counted per product, exactly, before anything is deleted. One query
 * listing every referencing row would be cut off at PostgREST's max-rows, and a
 * product whose lines fell past the cut would look unused. That is the one case
 * that must never slip through: transaction_items and return_items are ON
 * DELETE SET NULL, so its invoice lines would be blanked without an error.
 * Per-product counts cost more requests and cannot be wrong that way.
 *
 * The counts run under the caller's RLS, so they are only complete for someone
 * who can see every transaction. The Inventory page withholds this from
 * scope-limited roles for that reason: a rayon-limited user would see a product
 * sold only in another rayon as unused.
 *
 * Deletes ask for the removed rows back. RLS on DELETE does not raise, it just
 * matches nothing, so comparing what came back with what was sent is the only
 * way to notice a refusal.
 */
export async function hardDeleteUnusedProducts(ids: string[]): Promise<BulkHardDeleteResult> {
  const unique = [...new Set(ids)].filter(Boolean);
  const checked = await mapLimit(unique, USAGE_CONCURRENCY, async (id) => ({
    id,
    used: isProductUsed(await getProductUsage(id)),
  }));
  const inUse = checked.filter((c) => c.used).map((c) => c.id);
  const free = checked.filter((c) => !c.used).map((c) => c.id);

  const deleted: string[] = [];
  const images: (string | null)[] = [];
  const fail = (message: string) =>
    new Error(`${deleted.length} dari ${free.length} produk sudah terhapus, lalu gagal: ${message}`);

  for (const chunk of chunkIds(free)) {
    const { data, error } = await supabase
      .from('products').delete().in('id', chunk).select('id, image_url');
    if (!error) {
      for (const r of data ?? []) {
        deleted.push(r.id);
        images.push(r.image_url);
      }
      continue;
    }
    if (error.code !== '23503') throw fail(error.message);
    // Something started referencing one of these between the count and the
    // delete -- an allocation or a promo, both ON DELETE RESTRICT -- and the
    // whole statement was refused. Go one at a time so a single newcomer does
    // not hold back the other ninety-nine.
    for (const id of chunk) {
      const one = await supabase.from('products').delete().eq('id', id).select('id, image_url');
      if (one.error?.code === '23503') {
        inUse.push(id);
        continue;
      }
      if (one.error) throw fail(one.error.message);
      for (const r of one.data ?? []) {
        deleted.push(r.id);
        images.push(r.image_url);
      }
    }
  }

  // After the rows are gone, not before: an orphaned file only costs disk, but
  // a surviving row pointing at a deleted file is a broken image.
  // removeStoredImage swallows its own failures.
  await mapLimit(images, USAGE_CONCURRENCY, (img) => removeStoredImage(img));

  const gone = new Set(deleted);
  const used = new Set(inUse);
  return { deleted, inUse, untouched: free.filter((id) => !gone.has(id) && !used.has(id)) };
}
